import { guestApiClient } from './apiClient';
import { BookingDetails, ApiResponse, ApiError } from '../types';
import { getCurrentHotelCode } from '../utils/hotelContext';
import { isTokenValid, loadToken, clearToken, getBookingReferenceFromToken } from '../utils/tokenUtils';

export interface BookingLookupRequest {
  booking_code: string;
  /** Cloudflare Turnstile token (optional — backend requires it only after repeated failures). */
  cf_turnstile_token?: string | null;
}

export interface MobileVerificationRequest {
  hotel_code: string;
  mobile_number: string;
  cf_turnstile_token?: string | null;
}

export interface OTPVerificationRequest {
  hotel_code: string;
  mobile_number: string;
  otp: string;
  cf_turnstile_token?: string | null;
}

export interface AuthResponse {
  booking: BookingDetails;
  access_token: string;
  hotel_id: string;
  reservation_id?: string;
  is_checked_in?: boolean;
  check_in_status?: string;
  can_access_services?: boolean;
}

// Internal response format from guest auth API
interface GuestAuthApiResponse {
  success: boolean;
  data: {
    booking: BookingDetails;
    access_token: string;
    hotel_id: string;
    is_checked_in?: boolean;
    check_in_status?: string;
    can_access_services?: boolean;
  };
}

export interface OTPResponse {
  message: string;
  expires_in: number; // seconds
}

class AuthService {
  /**
   * Authenticate guest using booking reference code
   */
  async authenticateWithBookingCode(request: BookingLookupRequest): Promise<ApiResponse<AuthResponse> | ApiError> {
    try {
      const hotelCode = getCurrentHotelCode();

      console.log('Attempting guest authentication with:', {
        booking_code: request.booking_code,
        hotel_code: hotelCode,
        url: `${process.env.REACT_APP_API_URL}/api/guest/auth/login/`
      });

      const payload: any = {
        reference: request.booking_code,
        guest_access: true
      };

      // Include hotel code if available (but the guest auth endpoint finds hotel via booking)
      if (hotelCode) {
        payload.hotel_code = hotelCode;
      }

      if (request.cf_turnstile_token) {
        payload.cf_turnstile_token = request.cf_turnstile_token;
      }

      // Use guest authentication endpoint that returns JWT token
      const response = await guestApiClient.post<GuestAuthApiResponse>(`/api/guest/auth/login/`, payload);

      if (response.success && response.data?.data) {
        const authData = response.data.data;
        // Store reservation ID for POS order billing
        const reservationId = authData.booking.reference_number;
        this.storeReservationContext(reservationId);
        
        // Set authentication in API client with reservation context
        guestApiClient.setAuth(authData.access_token, authData.hotel_id, reservationId);
        
        // Store complete authentication state with all response data
        this.storeAuthState(authData.booking, 'booking_code', {
          isCheckedIn: authData.is_checked_in || false,
          checkInStatus: authData.check_in_status || 'unknown',
          canAccessServices: authData.can_access_services !== false
        });
        
        // Return the flattened response format expected by frontend
        return {
          success: true,
          data: {
            booking: authData.booking,
            access_token: authData.access_token,
            hotel_id: authData.hotel_id,
            reservation_id: reservationId,
            is_checked_in: authData.is_checked_in || false,
            check_in_status: authData.check_in_status || 'unknown',
            can_access_services: authData.can_access_services !== false
          }
        };
      }

      return {
        success: false,
        error: 'Authentication failed'
      };
    } catch (error: any) {
      // Handle check-in requirement errors specifically
      if (error.error && error.check_in_required) {
        const checkInError = {
          success: false,
          error: error.error,
          check_in_required: true,
          reservation_status: error.reservation_status,
          check_in_status: error.check_in_status
        };
        throw checkInError;
      }
      throw error;
    }
  }

  /**
   * Send OTP to mobile number for hotel guest verification
   */
  async sendMobileOTP(request: MobileVerificationRequest): Promise<ApiResponse<OTPResponse>> {
    try {
      // Set hotel context for this request
      const tempClient = guestApiClient;
      
      const response = await tempClient.post<OTPResponse>('/api/guest/send-otp/', {
        hotel_code: request.hotel_code,
        mobile_number: request.mobile_number,
        ...(request.cf_turnstile_token ? { cf_turnstile_token: request.cf_turnstile_token } : {}),
      });

      return response;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Verify OTP and get guest booking information
   */
  async verifyMobileOTP(request: OTPVerificationRequest): Promise<ApiResponse<AuthResponse>> {
    try {
      const response = await guestApiClient.post<AuthResponse>('/api/guest/verify-otp/', {
        hotel_code: request.hotel_code,
        mobile_number: request.mobile_number,
        otp: request.otp,
        ...(request.cf_turnstile_token ? { cf_turnstile_token: request.cf_turnstile_token } : {}),
      });

      if (response.success && response.data) {
        // Store reservation ID for POS order billing
        const reservationId = response.data.booking.reference_number;
        this.storeReservationContext(reservationId);
        
        // Set authentication in API client with reservation context
        guestApiClient.setAuth(response.data.access_token, response.data.hotel_id, reservationId);
        
        // Store complete authentication state
        this.storeAuthState(response.data.booking, 'mobile_verification', {
          isCheckedIn: response.data.is_checked_in,
          checkInStatus: response.data.check_in_status,
          canAccessServices: response.data.can_access_services
        });
      }

      return response;
    } catch (error: any) {
      // Handle check-in requirement errors specifically for OTP verification
      if (error.error && error.check_in_required) {
        const checkInError = {
          success: false,
          error: error.error,
          check_in_required: true,
          reservation_status: error.reservation_status,
          check_in_status: error.check_in_status
        };
        throw checkInError;
      }
      throw error;
    }
  }

  /**
   * Authenticate guest using secure token from email URL
   */
  async authenticateWithSecureToken(secureToken: string): Promise<ApiResponse<AuthResponse> | ApiError> {
    try {
      console.log('Attempting guest authentication with secure token');

      // Call secure login endpoint
      const authResponse = await guestApiClient.post<{
        success: boolean;
        data: {
          booking: BookingDetails;
          access_token: string;
          hotel_id: string;
          is_checked_in: boolean;
          check_in_status: string;
          can_access_services: boolean;
          login_method: string;
        };
      }>('/api/guest/auth/secure-login/', {
        secure_token: secureToken
      });

      if (authResponse.success && authResponse.data) {
        const authData = authResponse.data.data;
        
        // Set authentication credentials
        guestApiClient.setAuth(authData.access_token, authData.hotel_id, authData.booking.id);

        // Store complete authentication state
        this.storeAuthState(authData.booking, 'secure_token', {
          isCheckedIn: authData.is_checked_in,
          checkInStatus: authData.check_in_status,
          canAccessServices: authData.can_access_services
        });

        console.log('Secure token authentication successful');

        return {
          success: true,
          data: {
            booking: authData.booking,
            access_token: authData.access_token,
            hotel_id: authData.hotel_id,
            is_checked_in: authData.is_checked_in,
            check_in_status: authData.check_in_status,
            can_access_services: authData.can_access_services
          }
        };
      } else {
        throw new Error('Authentication failed');
      }
    } catch (error: any) {
      console.error('Secure token authentication failed:', error);
      
      // apiClient throws a normalised ApiError with the raw response in
      // `body`; keep supporting a raw axios error shape for safety.
      const responseBody = error.body || error.response?.data || {};
      const errorResponse = {
        success: false,
        error: responseBody.error || error.error || error.message || 'Secure authentication failed',
        check_in_required: responseBody.check_in_required || false,
        reservation_status: responseBody.reservation_status,
        check_in_status: responseBody.check_in_status,
        // 24h-post-checkout cut-off: the app lands the guest on the
        // hotel's public info page instead of a dead-end error.
        error_code: responseBody.error_code,
        hotel: responseBody.hotel,
      };

      throw errorResponse;
    }
  }

  /**
   * Self check-out identity check for hotels that verify by room number
   * (optionally plus a room-wise custom value such as a door code)
   * instead of the booking code. Returns the same shape as the other
   * logins and stores the session identically.
   */
  async verifyCheckout(request: {
    hotel_code: string;
    room_number: string;
    value?: string;
    cf_turnstile_token?: string | null;
  }): Promise<ApiResponse<AuthResponse> | ApiError> {
    try {
      const { cf_turnstile_token, ...fields } = request;
      const payload = cf_turnstile_token ? { ...fields, cf_turnstile_token } : fields;
      const response = await guestApiClient.post<{
        success: boolean;
        data: {
          booking: BookingDetails;
          access_token: string;
          hotel_id: string;
          is_checked_in: boolean;
          check_in_status: string;
          can_access_services: boolean;
          login_method: string;
        };
      }>('/api/guest/checkout/verify/', payload);

      if (response.success && response.data) {
        const authData = (response.data as any).data;
        guestApiClient.setAuth(authData.access_token, authData.hotel_id, authData.booking.id);
        this.storeAuthState(authData.booking, 'booking_code', {
          isCheckedIn: authData.is_checked_in,
          checkInStatus: authData.check_in_status,
          canAccessServices: authData.can_access_services,
        });
        return {
          success: true,
          data: {
            booking: authData.booking,
            access_token: authData.access_token,
            hotel_id: authData.hotel_id,
            is_checked_in: authData.is_checked_in,
            check_in_status: authData.check_in_status,
            can_access_services: authData.can_access_services,
          },
        };
      }
      throw new Error('Verification failed');
    } catch (error: any) {
      const body = error.body || error.response?.data || {};
      const errorResponse = {
        success: false,
        error: body.error || error.error || error.message || 'Verification failed',
        error_code: body.error_code,
        // Captcha / lockout responses (403 captcha_required|captcha_failed,
        // 429 login_locked) carry `code` + `detail` — keep them so the UI
        // can show the backend's message and reset the widget.
        code: body.code || error.code,
        body,
      };
      throw errorResponse;
    }
  }

  /**
   * QR-only fallback: sign in with the guest's full name as the channel
   * sent it, when the OTA booking number they hold doesn't match ours.
   * The backend scopes the match to the QR's hotel and to current stays.
   */
  async authenticateWithGuestName(request: {
    hotel_code: string;
    guest_name: string;
    cf_turnstile_token?: string | null;
  }): Promise<ApiResponse<AuthResponse> | ApiError> {
    try {
      const { cf_turnstile_token, ...fields } = request;
      const payload = cf_turnstile_token ? { ...fields, cf_turnstile_token } : fields;
      const response = await guestApiClient.post<{
        success: boolean;
        data: {
          booking: BookingDetails;
          access_token: string;
          hotel_id: string;
          is_checked_in: boolean;
          check_in_status: string;
          can_access_services: boolean;
        };
      }>('/api/guest/auth/name-login/', payload);

      if (response.success && response.data) {
        const authData = (response.data as any).data;
        guestApiClient.setAuth(authData.access_token, authData.hotel_id, authData.booking.id);
        this.storeAuthState(authData.booking, 'booking_code', {
          isCheckedIn: authData.is_checked_in,
          checkInStatus: authData.check_in_status,
          canAccessServices: authData.can_access_services,
        });
        return {
          success: true,
          data: {
            booking: authData.booking,
            access_token: authData.access_token,
            hotel_id: authData.hotel_id,
            is_checked_in: authData.is_checked_in,
            check_in_status: authData.check_in_status,
            can_access_services: authData.can_access_services,
          },
        };
      }
      throw new Error('Name login failed');
    } catch (error: any) {
      const body = error.body || error.response?.data || {};
      throw {
        success: false,
        error: body.error || error.error || error.message || 'Name login failed',
        error_code: body.error_code,
        code: body.code || error.code,
        body,
      };
    }
  }

  /**
   * Get current booking details (requires authentication)
   */
  async getCurrentBooking(): Promise<ApiResponse<BookingDetails>> {
    try {
      const response = await guestApiClient.get<BookingDetails>('/api/guest/booking/');
      return response;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Validate current session
   */
  async validateSession(): Promise<ApiResponse<{ valid: boolean; booking: BookingDetails }>> {
    try {
      const response = await guestApiClient.get<{ valid: boolean; booking: BookingDetails }>('/api/guest/validate-session/');
      console.log('Raw validateSession response:', response);
      return response;
    } catch (error: any) {
      console.error('validateSession error:', error);
      throw error;
    }
  }

  /**
   * Logout and clear session
   */
  async logout(): Promise<void> {
    try {
      await guestApiClient.post('/api/guest/logout/');
    } catch (error) {
      // Continue with logout even if API call fails
      console.warn('Logout API call failed:', error);
    } finally {
      this.clearAuth();
    }
  }

  /**
   * Clear all authentication data
   */
  clearAuth(): void {
    guestApiClient.clearAuth();
    this.clearReservationContext();
    this.clearStoredAuthState();
    clearToken('guest_token');
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return guestApiClient.isAuthenticated();
  }

  /**
   * Get hotel context (now handled by backend middleware from JWT token)
   */
  getHotelContext(): string | null {
    // Hotel context is now extracted from JWT token by backend middleware
    // No longer needed on frontend side
    return null;
  }

  /**
   * Update guest profile for pre-check-in
   */
  async updateGuestProfile(profileData: {
    address: string;
    city: string;
    country: string;
    special_requests: string;
  }): Promise<ApiResponse<{
    guest_id: string;
    reservation_id: string;
    updated_fields: string[];
    profile: {
      first_name: string;
      last_name: string;
      email: string;
      mobile_number: string;
      address: string;
      city: string;
      country: string;
      special_requests: string;
    };
  }>> {
    try {
      console.log('Updating guest profile:', profileData);
      const response = await guestApiClient.post<{
        guest_id: string;
        reservation_id: string;
        updated_fields: string[];
        profile: {
          first_name: string;
          last_name: string;
          email: string;
          mobile_number: string;
          address: string;
          city: string;
          country: string;
          special_requests: string;
        };
      }>('/api/guest/pre-checkin/update-profile/', profileData);
      
      if (response.success) {
        console.log('Profile update successful');
        return response as ApiResponse<{
          guest_id: string;
          reservation_id: string;
          updated_fields: string[];
          profile: {
            first_name: string;
            last_name: string;
            email: string;
            mobile_number: string;
            address: string;
            city: string;
            country: string;
            special_requests: string;
          };
        }>;
      } else {
        throw new Error('Profile update failed');
      }
    } catch (error: any) {
      console.error('Profile update failed:', error);
      
      const errorResponse = {
        success: false,
        error: error.response?.data?.error || error.message || 'Profile update failed',
      };
      
      throw errorResponse;
    }
  }

  /**
   * Attempt to recover session using stored credentials
   */
  async attemptSessionRecovery(): Promise<boolean> {
    try {
      console.log('Attempting session recovery...');
      
      // Try to load and validate stored token
      const storedToken = loadToken('guest_token');
      if (!storedToken) {
        console.log('No valid stored token for recovery');
        return false;
      }

      // Check if token is still valid
      if (!isTokenValid(storedToken)) {
        console.log('Stored token is invalid, clearing');
        clearToken('guest_token');
        this.clearStoredAuthState();
        return false;
      }

      // Try to validate the session with backend
      // Extract booking reference from JWT token, fallback to stored auth state
      let bookingReference = getBookingReferenceFromToken(storedToken);
      
      // If booking reference is not in token, try to get it from stored auth state
      if (!bookingReference) {
        const storedAuthState = this.getStoredAuthState();
        bookingReference = storedAuthState?.booking?.reference_number || storedAuthState?.lastBookingCode;
      }
      
      // Set authentication with all required context from token
      guestApiClient.setAuth(storedToken, undefined, bookingReference || undefined);
      const sessionValidation = await this.validateSession();
      
      console.log('Session validation response:', sessionValidation);
      console.log('Session validation response.data:', sessionValidation.data);
      console.log('Session validation success:', sessionValidation.success);
      console.log('Session validation data valid:', sessionValidation.data?.valid);
      console.log('Session validation data keys:', sessionValidation.data ? Object.keys(sessionValidation.data) : 'no data');
      
      // Handle double-wrapped response structure
      const responseData = sessionValidation.data as any;
      const isValid = sessionValidation.success && 
                     (responseData?.valid || responseData?.data?.valid);
      
      if (isValid) {
        console.log('Session recovery successful');
        return true;
      } else {
        console.log('Session validation failed during recovery - clearing auth');
        console.log('Condition failed: success =', sessionValidation.success, ', valid =', responseData?.valid, ', nested valid =', responseData?.data?.valid);
        this.clearAuth();
        return false;
      }
    } catch (error) {
      console.error('Session recovery failed:', error);
      this.clearAuth();
      return false;
    }
  }

  /**
   * Attempt silent re-authentication using stored booking code
   */
  async attemptSilentReauth(): Promise<boolean> {
    try {
      const storedAuthState = this.getStoredAuthState();
      if (!storedAuthState || !storedAuthState.booking) {
        return false;
      }

      console.log('Attempting silent re-authentication...');
      
      // Try to re-authenticate using the stored booking reference
      const response = await this.authenticateWithBookingCode({
        booking_code: storedAuthState.booking.reference_number
      });

      if (response.success) {
        console.log('Silent re-authentication successful');
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Silent re-authentication failed:', error);
      return false;
    }
  }

  /**
   * Comprehensive session recovery strategy
   */
  async recoverSession(): Promise<boolean> {
    console.log('Starting comprehensive session recovery...');
    
    // Strategy 1: Try to recover using existing valid token
    const tokenRecovery = await this.attemptSessionRecovery();
    if (tokenRecovery) {
      console.log('Session recovered using existing token');
      return true;
    }

    // Strategy 2: Try silent re-authentication
    const silentReauth = await this.attemptSilentReauth();
    if (silentReauth) {
      console.log('Session recovered using silent re-authentication');
      return true;
    }

    console.log('All session recovery strategies failed');
    return false;
  }

  /**
   * Store complete authentication state for persistence across page refreshes
   */
  private storeAuthState(
    booking: BookingDetails, 
    accessMethod: 'booking_code' | 'mobile_verification' | 'secure_token',
    additionalData?: {
      isCheckedIn?: boolean;
      checkInStatus?: string;
      canAccessServices?: boolean;
    }
  ): void {
    const authState = {
      isAuthenticated: true,
      booking,
      accessMethod,
      isCheckedIn: additionalData?.isCheckedIn || false,
      checkInStatus: additionalData?.checkInStatus || 'unknown',
      canAccessServices: additionalData?.canAccessServices || false,
      timestamp: Date.now(),
      // Store additional recovery data
      lastBookingCode: booking.reference_number,
      lastHotelCode: getCurrentHotelCode(),
    };
    localStorage.setItem('guest_auth_state', JSON.stringify(authState));
  }

  /**
   * Update the persisted check-in status after a successful self
   * check-in so refreshes and new tabs land on the right dashboard.
   */
  updateCheckInStatus(checkInStatus: string): void {
    try {
      const stored = localStorage.getItem('guest_auth_state');
      if (!stored) return;
      const authState = JSON.parse(stored);
      authState.isCheckedIn = checkInStatus === 'checked_in';
      authState.checkInStatus = checkInStatus;
      if (authState.isCheckedIn) {
        authState.canAccessServices = true;
      }
      localStorage.setItem('guest_auth_state', JSON.stringify(authState));
    } catch (error) {
      console.error('Failed to update stored check-in status:', error);
    }
  }

  /**
   * Load stored authentication state with validation
   */
  getStoredAuthState(): any | null {
    try {
      const stored = localStorage.getItem('guest_auth_state');
      if (stored) {
        const authState = JSON.parse(stored);
        
        // Check if state is not too old (7 days for guest sessions)
        const maxAge = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds
        if (Date.now() - authState.timestamp < maxAge) {
          // Validate that essential data is present
          if (authState.booking && authState.booking.reference_number) {
            return authState;
          } else {
            console.warn('Stored auth state missing essential data');
            this.clearStoredAuthState();
          }
        } else {
          console.log('Stored auth state expired, clearing');
          this.clearStoredAuthState();
        }
      }
    } catch (error) {
      console.error('Error loading stored auth state:', error);
      this.clearStoredAuthState();
    }
    return null;
  }

  /**
   * Check if we have sufficient data for session recovery
   */
  canAttemptRecovery(): boolean {
    const storedState = this.getStoredAuthState();
    return storedState && 
           storedState.booking && 
           storedState.booking.reference_number && 
           storedState.lastBookingCode;
  }

  /**
   * Get recovery metadata
   */
  getRecoveryMetadata(): { bookingCode?: string; hotelCode?: string } | null {
    const storedState = this.getStoredAuthState();
    if (!storedState) return null;
    
    return {
      bookingCode: storedState.lastBookingCode,
      hotelCode: storedState.lastHotelCode
    };
  }

  /**
   * Clear stored authentication state
   */
  private clearStoredAuthState(): void {
    localStorage.removeItem('guest_auth_state');
  }

  /**
   * Store reservation context for POS order billing
   */
  private storeReservationContext(reservationId: string): void {
    localStorage.setItem('guest_reservation_id', reservationId);
  }

  /**
   * Get stored reservation ID
   */
  getReservationId(): string | null {
    return localStorage.getItem('guest_reservation_id');
  }

  /**
   * Clear reservation context
   */
  private clearReservationContext(): void {
    localStorage.removeItem('guest_reservation_id');
  }
}

export const authService = new AuthService();